import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { StyledTableRow, StyledTableCell } from "../../sales/sales.styles";
import ReactToPrint from "react-to-print";
import {
  addRupeeSymbol,
  parseNumber,
} from "../../../utils/Utils";
import {
  useFetchDocById,
  useGetDocsByQuery,
} from "../../../components/Firebase/useFirebaseQueries";
import useLocalStorage from "../../../Hooks/UseLocalstorage";

const ReturnBillTable = ({ rows }) => {
  const componentRef = useRef();
  const reactToPrintRef = useRef();
  const [pharmacyId] = useLocalStorage("pharmacyId");
  const [selectedBill, setSelectedBill] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const pharmacyConditions = useMemo(() => [
    { field: "pharmacyId", operator: "==", value: pharmacyId },
  ], [pharmacyId]);

  const { data: patientData } = useFetchDocById(
    "patients",
    patientId,
    !!patientId
  );

  const { data: [pharmacy] = [], isLoading: pharmacyLoading } =
    useGetDocsByQuery("pharmacyList", pharmacyConditions, true);

  const shouldPrintRef = useRef(false);

  useEffect(() => {
    if (patientData && selectedBill && pharmacy) {
      setTimeout(() => {
        if (shouldPrintRef.current) {
          reactToPrintRef.current?.handlePrint();
          shouldPrintRef.current = false;
        }
      }, 0);
    }
  }, [patientData, selectedBill, pharmacy]);

  const handlePrint = (bill) => {
    shouldPrintRef.current = true;
    setSelectedBill({ ...bill });
    setPatientId(bill?.patientId || null);
  };

  // Helper to split array into chunks for pagination
  function paginate(array, pageSize) {
    return array.reduce((acc, _, i) => {
      if (i % pageSize === 0) acc.push(array.slice(i, i + pageSize));
      return acc;
    }, []);
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        overflowY: "auto",
        width: "100%",
        maxHeight: '60vh',
      }}
    >
      <Table stickyHeader sx={{ minWidth: 500 }} aria-label="customized table">
        <TableHead>
          <TableRow sx={{ backgroundColor: "#B4B4B4" }}>
            <StyledTableCell>S No</StyledTableCell>
            <StyledTableCell align="center">Return Bill Number</StyledTableCell>
            <StyledTableCell align="center">Date</StyledTableCell>
            <StyledTableCell align="center">Patient Name</StyledTableCell>
            <StyledTableCell align="center">Total Amount</StyledTableCell>
            <StyledTableCell align="center">Print</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <StyledTableRow key={row.id}>
              <StyledTableCell>{index + 1}</StyledTableCell>
              <StyledTableCell align="center">{row.returnBillNumber}</StyledTableCell>
              <StyledTableCell align="center">{row.returnDate}</StyledTableCell>
              <StyledTableCell align="center">{row.patientName}</StyledTableCell>
              <StyledTableCell align="center">
                {addRupeeSymbol(parseNumber(row.totalAmount))}
              </StyledTableCell>
              <StyledTableCell align="center">
                <PrintOutlinedIcon
                  color="primary"
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => handlePrint(row)}
                />
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
      <ReactToPrint
        ref={reactToPrintRef}
        trigger={() => <button style={{ display: "none" }}>Print Bill</button>}
        content={() => componentRef.current}
      />
      <Box sx={{ display: "none" }}>
        <div
          ref={componentRef}
          style={{ padding: "20px", fontFamily: "Arial" }}
        >
          <h1 style={{ textAlign: "center" }}>{pharmacy?.name}</h1>
          <p style={{ textAlign: "center" }}>{pharmacy?.address}</p>
          <p style={{ textAlign: "center" }}>{pharmacy?.phone}</p>
          <h3 style={{ textAlign: "center" }}>Return Bill Receipt</h3>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ marginLeft: "30px", textAlign: "left" }}>
              <p>
                <strong>Patient Name:</strong> {patientData?.firstName || selectedBill?.patientName || "N/A"}
              </p>
              <p>
                <strong>Mobile Number:</strong> {patientData?.phoneNumber || "N/A"}
              </p>
              <p>
                <strong>Patient ID:</strong> {patientData?.patientId || "N/A"}
              </p>
            </div>
            <div style={{ marginRight: "30px", textAlign: "left" }}>
              <p>
                <strong>Return Bill Number:</strong> {selectedBill?.returnBillNumber}
              </p>
              <p>
                <strong>Return Date:</strong> {selectedBill?.returnDate}
              </p>
            </div>
          </div>
          {selectedBill?.medicinesReturned && (
            <>
              {(() => {
                const pageSize = 10;
                const pages = paginate(selectedBill.medicinesReturned, pageSize);
                return pages.map((medicinesPage, pageIndex) => (
                  <div key={pageIndex} style={pageIndex !== pages.length - 1 ? { pageBreakAfter: 'always' } : {}}>
                    <table border="1" cellPadding="10" cellSpacing="0" width="100%">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Item</th>
                          <th>Batch</th>
                          <th>HSN Code</th>
                          <th>Exp</th>
                          <th>Price</th>
                          <th>Return Qty</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {medicinesPage.map((medicine, index) => (
                          <tr key={index}>
                            <td>{pageIndex * pageSize + index + 1}</td>
                            <td>
                              {medicine.brandName}
                              <br />
                              <em>
                                <span style={{ fontSize: "0.85em" }}>
                                  ({medicine.pharmacologicalName || "N/A"})
                                </span>
                              </em>
                            </td>
                            <td>{medicine.batchNo || "N/A"}</td>
                            <td>{medicine.hsnCode || "N/A"}</td>
                            <td>{medicine.expiryDate || "N/A"}</td>
                            <td>{addRupeeSymbol(parseNumber(medicine.price))}</td>
                            <td>{medicine.returnQuantity}</td>
                            <td>{addRupeeSymbol(parseNumber(medicine.returnQuantity * medicine.price))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {pageIndex === pages.length - 1 && (
                      <div style={{ textAlign: "right", marginTop: "20px" }}>
                        <p>
                          <strong>Total Return Amount:</strong> {addRupeeSymbol(parseNumber(selectedBill?.totalAmount))}
                        </p>
                      </div>
                    )}
                  </div>
                ));
              })()}
            </>
          )}
        </div>
      </Box>
    </TableContainer>
  );
};

export default ReturnBillTable;